import React, { useEffect, useRef, useState, useCallback } from "react";
import ChatBubble from "../chatComponents/ChatBubble";

function ChatTranscript({
                            conversationHistory: conversationHistoryProp,
                            refreshTranscript,
                            onSummaryConfirmationNeeded,
                            conversationId,
                            updateSummaryData,
                            onNoConfirm,
                            setIsEditing,
                            isSavingChanges
                        }) {
    const [editMode, setEditMode] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [showButtons, setShowButtons] = useState(true);
    const [isInitialMessage, setFirstMessage] = useState(true);
    const initialMessage = {
        role: "assistant",
        content: "Please review your summary. Would you like to make any changes to it?",
    };
    const [conversationHistory, setConversationHistory] = useState(conversationHistoryProp ? [...conversationHistoryProp.slice(1), initialMessage] : [initialMessage]);
    const messagesEndRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const handlePostUpdate = useCallback(() => {
        setEditMode(false);
        setShowButtons(true);
        setFirstMessage(false); // Adjust based on your desired flow
        setConversationHistory(prevHistory => [
            ...prevHistory,
            { role: "assistant", content: "Please see the updated summary. Would you like to further edit it?" }
        ]);
        scrollToBottom();
    }, [scrollToBottom]);

    useEffect(() => {
        if (refreshTranscript) {
            handlePostUpdate();
        }
    }, [refreshTranscript, handlePostUpdate]);

    useEffect(() => {
        setConversationHistory(conversationHistoryProp ? [...conversationHistoryProp.slice(1), initialMessage] : [initialMessage]);
    }, [conversationHistoryProp]);


    useEffect(() => {
        setIsEditing(editMode);
    }, [editMode, setIsEditing]);

    useEffect(() => {
        scrollToBottom();
    }, [conversationHistory, editMode, scrollToBottom]);

    const handleNoClick = useCallback(() => {
        if (onNoConfirm) {
            onNoConfirm();
        }
    }, [onNoConfirm]);

    const handleYesClick = useCallback(() => {
        setEditMode(true);
        setShowButtons(false);
        setFirstMessage(false);
        setConversationHistory(prevHistory => [
            ...prevHistory,
            { role: "assistant", content: "What would you like to change?" }
        ]);
        scrollToBottom();
    }, [scrollToBottom]);

    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value);
    }, []);

    const handleKeyPress = useCallback(async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            setInputValue("");
            e.preventDefault();
            if (!inputValue.trim()) return;
            const requestBody = {
                ID: conversationId,
                content: inputValue.trim(),
                firstMessage: isInitialMessage,
            };
            const response = await fetch("https://www.cs.toronto.edu/~brli/intakebot/api/edit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const data = await response.json();
                setConversationHistory(currentHistory => [
                    ...currentHistory,
                    { role: "user", content: inputValue.trim() },
                    ...(data.result && data.result.role === "assistant" ? [{ role: data.result.role, content: data.result.content }] : [])
                ]);
                setInputValue("");
                scrollToBottom();
            }
        }
    }, [inputValue, conversationId, isInitialMessage, scrollToBottom]);

    return (
        <div className='chatTranscriptContent'>
            <div className='chatTranscriptMessages'>
                {conversationHistory.map((message, index) => (
                    <ChatBubble key={index} type={message.role === "assistant" ? "bot" : "user"}>
                        {message.content}
                    </ChatBubble>
                ))}
                <div ref={messagesEndRef} />
            </div>
            {showButtons && (
                <div className='decisionButtons'>
                    <button className='noButton' onClick={handleNoClick} disabled={isSavingChanges}>No, it looks ready
                        to be sent
                    </button>
                    <button className='yesButton' onClick={handleYesClick} disabled={isSavingChanges}>Yes, I want to
                        make a change
                    </button>
                </div>
            )}
            {editMode && (
                <div className='chatTranscriptInput' style={{display: 'flex', alignItems: 'center'}}>
                    <input
                        type='text'
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder='Type a message...'
                        style={{ flex: 1 }}
                        disabled={isSavingChanges}
                    />
                </div>
            )}
        </div>
    );
}

export default ChatTranscript;
